/**
 * Where the "Resequencing preview" switch starts, as a pure module (story 177, contract 03-dominio.md §6.1 and
 * §6.4; S155, S158, S162). `useResequencePreviewPreference` imports it; nothing here reads, writes or renders.
 *
 * Precedence, frozen:
 *   1. the user's saved choice (`RESEQUENCE_PREVIEW.show` in `user/tabletune/PlannerDetailsResequencePreview`),
 *      when it is there and is a boolean;
 *   2. the plant value `frontEndConfig.plannerResequencePreviewDefault`, read with `!== false`: only the boolean
 *      `false` turns the switch off; `true`, `null`, a missing key (a Sun older than the story), the text
 *      "false", or a configuration not loaded yet (`null`) leave it on;
 *   3. on.
 * The plant value is never written as the user's choice (risk R30): only a click makes a choice. And there is no
 * way back from a choice to "follow the plant" (S162): `chooseResequencePreview` is the only way to a choice,
 * and a choice does not expire.
 *
 * A "user choice" is the object `{ chosen, show }`. While `chosen` is false the switch FOLLOWS the plant value:
 * when `frontEndConfig` arrives later, `resolveResequencePreview` gives the new value (S158). From the first
 * click, `chosen` is true and the plant value no longer matters.
 */

export const PREVIEW_TABLE_KEY = 'PlannerDetailsResequencePreview'
export const PREVIEW_FIELD_NAME = 'RESEQUENCE_PREVIEW'

/** No choice yet: the switch follows the plant value. */
export const NO_USER_CHOICE = Object.freeze({ chosen: false, show: true })

/** The plant value: `false` only for the boolean `false` (contract §6.4, V9). */
export const plantResequencePreviewDefault = (frontEndConfig) => frontEndConfig?.plannerResequencePreviewDefault !== false

/**
 * The user's saved choice out of the answer of `GET user/tabletune/PlannerDetailsResequencePreview`.
 * An error, a missing row, `fields: []`, a field with another name or a `show` that is not a boolean is no
 * choice (S155): Jupiter does not retry nor warn.
 *
 * @param {Object} tuneAnswer What `apiGet` resolved with; anything else (an error, `{}`, nothing) is no choice
 * @returns {{ chosen: boolean, show: boolean }}
 */
export const savedResequencePreviewChoice = (tuneAnswer) => {
  const fields = Array.isArray(tuneAnswer?.fields) ? tuneAnswer.fields : []
  const field = fields.find((candidate) => candidate?.name === PREVIEW_FIELD_NAME)
  if (field && typeof field.show === 'boolean') return { chosen: true, show: field.show }
  return NO_USER_CHOICE
}

/** The user's click: from now on the choice is theirs, whatever the plant says (S156, S162). */
export const chooseResequencePreview = (show) => ({ chosen: true, show: show === true })

/** True while the switch still follows the plant value: nothing saved and nothing clicked in this session. */
export const followsPlantValue = (userChoice) => userChoice?.chosen !== true

/**
 * The state of the switch: the user's choice, else the plant value, else on.
 *
 * @param {{ chosen: boolean, show: boolean }} userChoice Saved or clicked; `NO_USER_CHOICE` if neither
 * @param {Object} frontEndConfig `useFrontEndConfig()`'s value; `null` while it arrives or if it failed
 * @returns {boolean}
 */
export const resolveResequencePreview = (userChoice, frontEndConfig) =>
  followsPlantValue(userChoice) ? plantResequencePreviewDefault(frontEndConfig) : userChoice.show === true

/**
 * The body of `POST user/tabletune` for a click (contract §6.1): the `id`s are `null` the first time and the
 * ones Sun returned afterwards. `null` is the API boundary here, as the contract freezes it.
 *
 * @param {boolean} show The switch's new state
 * @param {{ id: number, fieldId: number }} ids What the previous save returned (`{}` the first time)
 */
export const resequencePreviewPreferenceBody = (show, ids = {}) => ({
  id: Number.isInteger(ids.id) ? ids.id : null,
  tableKey: PREVIEW_TABLE_KEY,
  fields: [{ id: Number.isInteger(ids.fieldId) ? ids.fieldId : null, name: PREVIEW_FIELD_NAME, show: show === true, order: 1 }]
})
